import React, { useState, useCallback } from 'react';
import uniqBy from 'lodash/uniqBy';

// Constants
import TrackingConstants from 'Shared/constants/Tracking.constants';

// Hooks
import { useClientSideSearchData } from 'Shared/utils/hooks';
import { useInviteToWorkspace } from 'Shared/actions/inviteToWorkspace';
import useValidateEmployeeEmail from 'Shared/utils/hooks/use-validate-employee-email';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Types
import { Workspace } from 'Shared/types/Workspace.type';

// Helpers
import { validEmail } from 'Shared/helpers';

// Components
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import { WpForm } from 'Wpds/components/form';
import InviteCollaboratorsInput from 'Dashboard/components/InviteCollaboratorsInput/InviteCollaboratorsInput';
import WorkspaceSettingsContainer from './WorkspaceSettingsContainer';

// Styles
import './WorkspaceMembersInvite.scss';

type Props = {
  workspace: Workspace;
  handleBackNavigation: () => void;
};

const WorkspaceMembersInvite = ({ workspace, handleBackNavigation }: Props) => {
  const [inviteeEmails, setInviteeEmails] = useState([]);
  const { clientSideSearchData, isLoading: isLoadingInviteeOptions } = useClientSideSearchData();
  const { isEmployeeEmailOrValidDomain } = useValidateEmployeeEmail();
  const { isThirdPartyUser } = useCurrentUser();

  const { shareLink, id: workspaceId, isGroup } = workspace;

  const attendeeOptions = workspace.attendeesWithoutAccounts.map(email => ({ label: email, value: email }));

  const isAllEmailsValid = useCallback(() => (inviteeEmails || []).every(({ value }) => validEmail(value)), [
    inviteeEmails,
  ]);

  const areAllEmailWithinAllowedPiDomains = useCallback(
    () => (inviteeEmails || []).every(({ value }) => isEmployeeEmailOrValidDomain({ email: value })),
    [inviteeEmails]
  );
  const disableSubmitOnFailedValidation = !isAllEmailsValid() || !areAllEmailWithinAllowedPiDomains();
  // Autocomplete options are:
  // all coworkers + all atendees of the meeting who aren't in workpatterns
  // minus anyone who is already part of the workspace
  const coworkerOptions = clientSideSearchData
    .filter(({ coworker }) => coworker)
    .map(({ email }) => ({
      label: email,
      value: email,
    }));

  const inviteeOptions = uniqBy(attendeeOptions.concat(coworkerOptions), 'value').filter(
    ({ value }) => !workspace.users.find(({ email }) => email === value)
  );

  const [inviteToWorkspace, { loading }] = useInviteToWorkspace({
    inviteMethod: TrackingConstants.INVITE_METHOD_TYPES.INVITE_TO_EXISTING_GROUP_WORKSPACE,
    onSuccess: () => {
      setInviteeEmails([]);
      handleBackNavigation();
    },
  });

  const sendInviteEmails = useCallback(() => {
    const emails = inviteeEmails.map(({ value }) => value);
    inviteToWorkspace({ workspaceId, emails });
  }, [inviteToWorkspace, inviteeEmails, workspaceId]);

  const onCopyLink = () => {
    (window as any).analytics.track('Copied Invite Link', { is_group: true });
  };

  if (!isGroup) return null;

  const baseClassName = 'WorkspaceMembersInvite';

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__description`}>
        <WpButton variant="tertiary" size="sm" onClick={handleBackNavigation}>
          <WpIcon name="angle-left" />
          Back
        </WpButton>
      </div>
      {!isThirdPartyUser && (
        <WorkspaceSettingsContainer title="Invite with link" subTitle="Anyone with this link can join this workspace">
          <WpTextField
            id="workspace-share-link"
            label="Invite with link"
            value={shareLink}
            enableCopy
            disabled
            labelHidden
            onCopy={onCopyLink}
          />
        </WorkspaceSettingsContainer>
      )}
      <WorkspaceSettingsContainer title="Invite with email" subTitle=" We'll send them an email to join the workspace.">
        <WpForm>
          <div className={`${baseClassName}__email-form`}>
            <div className={`${baseClassName}__email-form__input`}>
              <InviteCollaboratorsInput
                value={inviteeEmails}
                options={inviteeOptions}
                label={false}
                onChange={setInviteeEmails}
                placeholder="Enter Emails"
                loading={isLoadingInviteeOptions}
              />
            </div>
            <div className={`${baseClassName}__email-form__button`}>
              <WpButton
                size="md"
                loading={loading}
                disabled={!(inviteeEmails && inviteeEmails.length) || disableSubmitOnFailedValidation}
                onClick={sendInviteEmails}
                qaTest="WorkspaceMembersInvite-invite-send-button"
              >
                <WpIcon name="paper-plane"></WpIcon>Invite
              </WpButton>
            </div>
          </div>
        </WpForm>
      </WorkspaceSettingsContainer>
    </div>
  );
};

export default WorkspaceMembersInvite;
